import React from "react";
import i18n from "i18next";

export default function MultiInputItemEditor({
  item,
  index,
  total,
  collapsed,
  onToggleCollapse,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  siblingScenes = [],
  navItemsById = {},
  viewToolbarsById = {},
}) {
  const update = (patch) => onChange({ ...item, ...patch });
  const updateValidation = (patch) => {
    const existing = item.validation || {};
    onChange({ ...item, validation: { ...existing, ...patch } });
  };
  const setValidationMode = (mode) => {
    if (mode === "exact") {
      onChange({
        ...item,
        validation: { mode: "exact", value: (item.validation && item.validation.value) || "" },
      });
    } else {
      onChange({ ...item, validation: { mode: "random" } });
    }
  };
  const isInput = item.kind === "input";
  const isTag = item.kind === "tag";
  const isClick = item.kind === "click";
  const labelStyle = {
    fontSize: 11,
    color: "#6b7280",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        onClick={onToggleCollapse}
        title={
          collapsed
            ? i18n.t("GuideMultiInput.expand_item") || "Expand"
            : i18n.t("GuideMultiInput.collapse_item") || "Collapse"
        }
        style={{
          padding: "0 4px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "#6b7280",
          fontSize: 14,
          width: 20,
        }}
      >
        {collapsed ? "▸" : "▾"}
      </button>
      <span
        style={{
          background: "#f3f4f6",
          color: "#374151",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {index + 1}
      </span>
      <span
        title={item.label || ""}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          color: "#6b7280",
        }}
      >
        {isInput
          ? i18n.t("GuideMultiInput.kind_input") || "Input"
          : isTag
            ? i18n.t("GuideMultiInput.kind_tag") || "Tag"
            : i18n.t("GuideMultiInput.kind_click") || "Click"}
        {item.label ? " · " + item.label : ""}
      </span>
      <button
        type="button"
        title={i18n.t("GuideMultiInput.move_up") || "Move up"}
        disabled={index === 0}
        onClick={onMoveUp}
        style={{
          padding: "2px 6px",
          border: "none",
          background: "transparent",
          cursor: index === 0 ? "not-allowed" : "pointer",
        }}
      >
        ↑
      </button>
      <button
        type="button"
        title={i18n.t("GuideMultiInput.move_down") || "Move down"}
        disabled={index === total - 1}
        onClick={onMoveDown}
        style={{
          padding: "2px 6px",
          border: "none",
          background: "transparent",
          cursor: index === total - 1 ? "not-allowed" : "pointer",
        }}
      >
        ↓
      </button>
      <button
        type="button"
        title={i18n.t("GuideMultiInput.delete_item") || "Delete"}
        onClick={onDelete}
        style={{
          padding: "2px 6px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "#b91c1c",
        }}
      >
        ×
      </button>
    </div>
  );

  return (
    <div
      className="mi-item-editor"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        padding: 10,
        marginBottom: 8,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {header}

      {!collapsed && (
        <>
          <label style={labelStyle}>
            {i18n.t("GuideMultiInput.label") || "Label"}
            <input
              type="text"
              value={item.label || ""}
              onChange={(e) => update({ label: e.target.value })}
              placeholder={i18n.t("GuideMultiInput.label_placeholder") || "Optional label"}
              style={{ padding: 6 }}
            />
          </label>

          <div style={labelStyle}>
            <span>{i18n.t("GuideMultiInput.item_color") || "Item Color"}</span>
            <input
              type="color"
              value={item.color || "#ff00a0"}
              onChange={(e) => update({ color: e.target.value })}
              style={{
                width: 48,
                height: 28,
                padding: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 4,
                alignSelf: "flex-start",
              }}
            />
          </div>

          {isInput && (
            <>
              <label style={labelStyle}>
                {i18n.t("GuideMultiInput.success_message") || "Success Message"}
                <input
                  type="text"
                  value={item.successMessage || ""}
                  onChange={(e) => update({ successMessage: e.target.value })}
                  placeholder={i18n.t("GuideMultiInput.success_placeholder") || "e.g. Great job!"}
                  style={{ padding: 6 }}
                />
              </label>

              <label style={labelStyle}>
                {i18n.t("GuideMultiInput.failure_message") || "Failure Message"}
                <input
                  type="text"
                  value={item.failureMessage || ""}
                  onChange={(e) => update({ failureMessage: e.target.value })}
                  placeholder={
                    i18n.t("GuideMultiInput.failure_placeholder") || "e.g. Check your spelling"
                  }
                  style={{ padding: 6 }}
                />
              </label>
            </>
          )}

          {isClick && (
            <>
              <label style={labelStyle}>
                {i18n.t("GuideMultiInput.on_click_label") || "On Click"}
                <select
                  value={item.onClickAction || "next"}
                  onChange={(e) => {
                    const val = e.target.value;
                    const patch = { onClickAction: val };
                    if (val !== "step") patch.targetStepId = null;
                    update(patch);
                  }}
                  style={{ padding: 6 }}
                >
                  <option value="next">
                    {i18n.t("GuideMultiInput.onClick_next_item") || "Go to next item"}
                  </option>
                  <option value="step">
                    {i18n.t("GuideMultiInput.onClick_step") || "Go to specific scene"}
                  </option>
                </select>
              </label>

              {item.onClickAction === "step" && (
                <label style={labelStyle}>
                  {i18n.t("GuideMultiInput.target_step_label") || "Target Scene"}
                  <select
                    value={item.targetStepId || ""}
                    onChange={(e) => update({ targetStepId: e.target.value || null })}
                    style={{ padding: 6 }}
                  >
                    <option value="">
                      {i18n.t("GuideMultiInput.pick_target") || "— Pick a scene —"}
                    </option>
                    {siblingScenes.map((sid, idx) => {
                      const vt = viewToolbarsById[sid];
                      const name =
                        (vt && vt.viewName) ||
                        (navItemsById[sid] && navItemsById[sid].name) ||
                        "Step " + (idx + 1);
                      return (
                        <option key={sid} value={sid}>
                          {idx + 1}. {name}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
            </>
          )}

          {isInput && (
            <>
              <label style={labelStyle}>
                {i18n.t("GuideMultiInput.tag") || "Tag (optional, for later reference)"}
                <input
                  type="text"
                  value={item.tag || ""}
                  onChange={(e) => update({ tag: e.target.value })}
                  placeholder={i18n.t("GuideMultiInput.tag_placeholder") || "e.g. firstName"}
                  style={{ padding: 6 }}
                />
              </label>

              <label style={labelStyle}>
                {i18n.t("GuideMultiInput.validation_mode") || "Validation Mode"}
                <select
                  value={(item.validation && item.validation.mode) || "random"}
                  onChange={(e) => setValidationMode(e.target.value)}
                  style={{ padding: 6 }}
                >
                  <option value="exact">
                    {i18n.t("GuideMultiInput.validation_exact") || "Exact match"}
                  </option>
                  <option value="random">
                    {i18n.t("GuideMultiInput.validation_random") || "Random length"}
                  </option>
                </select>
              </label>

              {item.validation && item.validation.mode === "exact" && (
                <label style={labelStyle}>
                  {i18n.t("GuideMultiInput.expected_value") || "Expected Value"}
                  <input
                    type="text"
                    value={item.validation.value || ""}
                    onChange={(e) => updateValidation({ value: e.target.value })}
                    style={{ padding: 6 }}
                  />
                </label>
              )}

              {item.validation && item.validation.mode === "random" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={labelStyle}>
                    {i18n.t("GuideMultiInput.min_chars") || "Min"}
                    <input
                      type="number"
                      min={0}
                      value={item.validation.minLength ?? ""}
                      onChange={(e) =>
                        updateValidation({
                          minLength: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                      style={{ padding: 6 }}
                    />
                  </label>
                  <label style={labelStyle}>
                    {i18n.t("GuideMultiInput.max_chars") || "Max"}
                    <input
                      type="number"
                      min={0}
                      value={item.validation.maxLength ?? ""}
                      onChange={(e) =>
                        updateValidation({
                          maxLength: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                      style={{ padding: 6 }}
                    />
                  </label>
                </div>
              )}
            </>
          )}

          {isTag && (
            <label style={labelStyle}>
              {i18n.t("GuideMultiInput.tag_ref") || "Tag Reference"}
              <input
                type="text"
                value={item.tagRef || ""}
                onChange={(e) => update({ tagRef: e.target.value })}
                placeholder={i18n.t("GuideMultiInput.tag_ref_placeholder") || "e.g. firstName"}
                style={{ padding: 6 }}
              />
            </label>
          )}
        </>
      )}
    </div>
  );
}
